export const locales = ["ko", "en"] as const;

export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "ko";
export const localeCookieName = "NEXT_LOCALE";
export const localeHeaderName = "x-locale";

export const isLocale = (value: string): value is Locale =>
  locales.includes(value as Locale);

export const getLocaleFromPathname = (pathname: string): Locale => {
  const [, firstSegment] = pathname.split("/");
  if (firstSegment && isLocale(firstSegment)) {
    return firstSegment;
  }
  return defaultLocale;
};

/** Internal app path like `/prompts` → `/ko/prompts` for middleware locale prefix. */
export function hrefWithLocale(locale: Locale, path: string): string {
  const normalized =
    path === "/" || path === ""
      ? ""
      : path.startsWith("/")
        ? path
        : `/${path}`;
  return `/${locale}${normalized}`;
}

type Dictionary = {
  header: {
    searchPlaceholder: string;
    prompts: string;
    profile: string;
    managePrompts: string;
    purchaseHistory: string;
    logout: string;
    login: string;
    signUp: string;
  };
  home: {
    heroTitleTop: string;
    heroTitleBottom: string;
    heroDescription: string;
    browsePrompts: string;
    becomeSeller: string;
    popularPrompts: string;
    viewAll: string;
    statPrompts: string;
    statActiveUsers: string;
    statSatisfaction: string;
    statSupport: string;
    footerTagline: string;
    footerService: string;
    footerBuyPrompts: string;
    footerSellPrompts: string;
    footerPremium: string;
    footerSupport: string;
    footerHelp: string;
    footerContact: string;
    footerFaq: string;
    footerCompany: string;
    footerTerms: string;
    footerPrivacy: string;
  };
  prompts: {
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    all: string;
    popular: string;
    newest: string;
    price: string;
    rating: string;
    filter: string;
    totalResults: string;
    noResults: string;
    tryAnotherKeyword: string;
  };
};

export const dictionary: Record<Locale, Dictionary> = {
  ko: {
    header: {
      searchPlaceholder: "프롬프트 검색...",
      prompts: "프롬프트",
      profile: "내 프로필",
      managePrompts: "프롬프트 관리",
      purchaseHistory: "구매 내역",
      logout: "로그아웃",
      login: "로그인",
      signUp: "회원가입",
    },
    home: {
      heroTitleTop: "최고의 AI 프롬프트를",
      heroTitleBottom: "발견하고 판매하세요",
      heroDescription:
        "전문가들이 만든 고품질 프롬프트를 구매하고, 나만의 프롬프트를 판매하여 수익을 창출하세요.",
      browsePrompts: "프롬프트 둘러보기",
      becomeSeller: "판매자 되기",
      popularPrompts: "인기 프롬프트",
      viewAll: "전체 보기",
      statPrompts: "프롬프트",
      statActiveUsers: "활성 사용자",
      statSatisfaction: "만족도",
      statSupport: "고객 지원",
      footerTagline: "최고의 AI 프롬프트 마켓플레이스",
      footerService: "서비스",
      footerBuyPrompts: "프롬프트 구매",
      footerSellPrompts: "프롬프트 판매",
      footerPremium: "프리미엄",
      footerSupport: "지원",
      footerHelp: "도움말",
      footerContact: "문의하기",
      footerFaq: "FAQ",
      footerCompany: "회사 정보",
      footerTerms: "이용약관",
      footerPrivacy: "개인정보처리방침",
    },
    prompts: {
      title: "프롬프트 마켓",
      subtitle: "전문가들이 만든 고품질 AI 프롬프트를 찾아보세요",
      searchPlaceholder: "프롬프트 검색...",
      all: "전체",
      popular: "인기순",
      newest: "최신순",
      price: "가격순",
      rating: "평점순",
      filter: "필터링하기",
      totalResults: "총 {count}개의 프롬프트",
      noResults: "검색 결과가 없습니다.",
      tryAnotherKeyword: "다른 키워드로 검색해보세요.",
    },
  },
  en: {
    header: {
      searchPlaceholder: "Search prompts...",
      prompts: "Prompts",
      profile: "My Profile",
      managePrompts: "Manage Prompts",
      purchaseHistory: "Purchase History",
      logout: "Log Out",
      login: "Log In",
      signUp: "Sign Up",
    },
    home: {
      heroTitleTop: "Discover top AI prompts",
      heroTitleBottom: "and sell your own",
      heroDescription:
        "Buy high-quality prompts made by experts, and monetize your own prompts in one marketplace.",
      browsePrompts: "Browse Prompts",
      becomeSeller: "Become a Seller",
      popularPrompts: "Popular Prompts",
      viewAll: "View All",
      statPrompts: "Prompts",
      statActiveUsers: "Active Users",
      statSatisfaction: "Satisfaction",
      statSupport: "Customer Support",
      footerTagline: "The best marketplace for AI prompts",
      footerService: "Service",
      footerBuyPrompts: "Buy Prompts",
      footerSellPrompts: "Sell Prompts",
      footerPremium: "Premium",
      footerSupport: "Support",
      footerHelp: "Help Center",
      footerContact: "Contact",
      footerFaq: "FAQ",
      footerCompany: "Company",
      footerTerms: "Terms of Service",
      footerPrivacy: "Privacy Policy",
    },
    prompts: {
      title: "Prompt Market",
      subtitle: "Explore high-quality AI prompts created by experts",
      searchPlaceholder: "Search prompts...",
      all: "All",
      popular: "Most Popular",
      newest: "Newest",
      price: "Price",
      rating: "Rating",
      filter: "Filter",
      totalResults: "{count} prompts found",
      noResults: "No results found.",
      tryAnotherKeyword: "Try searching with a different keyword.",
    },
  },
};
